import { CustomTableSortItems } from 'commons/components/table/sort/custom-table-sorting.model';
import { useMaterialDefinitionsList } from 'modules/settings/pods/settings-materials/service/material-definitions/hooks/use-material-definitions-list.hook';
import { PropsWithChildren, useCallback, useMemo, useState } from 'react';
import { toSortParam } from './material-definitions-list.business';
import { MaterialDefinitionsListContext } from './material-definitions-list.context';

const DEFAULT_SORT: CustomTableSortItems = { name: 'asc' };

export const MaterialDefinitionsListProvider: React.FC<PropsWithChildren> = ({ children }) => {
    const [sortItems, setSortItems] = useState<CustomTableSortItems>(DEFAULT_SORT);
    const sortParam = useMemo(() => toSortParam(sortItems), [sortItems]);
    const { materials, isLoading } = useMaterialDefinitionsList(sortParam);

    const [selectedRowIds, setSelectedRowIds] = useState<Set<string>>(new Set());

    const handleSelectRow = useCallback((id: string, selected: boolean) => {
        setSelectedRowIds((prev) => {
            const next = new Set(prev);
            if (selected) {
                next.add(id);
            } else {
                next.delete(id);
            }
            return next;
        });
    }, []);

    const clearSelection = useCallback(() => setSelectedRowIds(new Set()), []);

    const selectedMaterials = useMemo(
        () => materials.filter((material) => selectedRowIds.has(material.id)),
        [materials, selectedRowIds]
    );

    const value = useMemo(
        () => ({
            materials,
            isLoading,
            defaultSort: DEFAULT_SORT,
            onSortOrderChange: setSortItems,
            selectedRowIds,
            setSelectedRowIds,
            selectedMaterials,
            handleSelectRow,
            clearSelection,
        }),
        [materials, isLoading, selectedRowIds, selectedMaterials, handleSelectRow, clearSelection]
    );

    return <MaterialDefinitionsListContext.Provider value={value}>{children}</MaterialDefinitionsListContext.Provider>;
};
